"use client";

import NextImage from "next/image";
import NextLink from "next/link";
import { Button as ReactAriaButton } from "react-aria-components";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

import { Button, type ButtonProperties } from "@jetblue/kit/button";
import { Popover } from "@jetblue/kit/popover";

import { Ad, type AdProperties } from "../ad";
import { CallToActions } from "./call-to-actions";
import { constructReturnURL } from "../../../../../helpers/links/return-url";

import type { UnauthenticatedCallToActionsProperties } from "./call-to-actions";

export type UnauthenticatedProperties = {
  ad: AdProperties;
  button: {
    label: string;
    logo?: string | undefined;
  };
  callToActions: UnauthenticatedCallToActionsProperties;
  signIn: {
    href: string;
    label: string;
    variant?: ButtonProperties["variant"];
  };
};
export const Unauthenticated = ({
  ad,
  button: { label, logo },
  callToActions,
  signIn,
}: UnauthenticatedProperties) => {
  const signInButtonVariant = signIn.variant ?? "primary";

  const pathname = usePathname();
  const [href, setHref] = useState(constructReturnURL(signIn.href));

  useEffect(() => {
    setHref(constructReturnURL(signIn.href));
  }, [signIn.href, pathname]);

  return (
    <Popover
      className="size-full overflow-auto rounded-none border-none bg-white p-0 md:h-unset md:w-[375px] md:rounded-b-md md:shadow-jb-2"
      offset={0}
      placement="bottom right"
      showOverlayArrow={false}
      triggerButton={
        <ReactAriaButton
          aria-haspopup="dialog"
          className="relative flex h-full shrink-0 grow-0 cursor-pointer items-center justify-center gap-2 px-2 after:absolute after:bottom-0 after:h-1 after:w-0 after:bg-orange after:transition-all after:content-['_'] hover:after:w-full focus-visible:outline-1 focus-visible:outline-offset-[3px] focus-visible:outline-white aria-expanded:after:w-full"
          data-fs-element="main-nav-menu-button-user-unauthenticated"
        >
          {logo && (
            <span className="relative size-8">
              <NextImage alt="" fill role="presentation" src={logo} />
            </span>
          )}

          <span className="sr-only text-white lg:not-sr-only">{label}</span>
        </ReactAriaButton>
      }
    >
      <div className="p-6">
        <Button
          as={NextLink}
          data-fs-element="user-unauthenticated-sign-in-link"
          href={href}
          isFullWidth
          size="large"
          variant={signInButtonVariant}
        >
          {signIn.label}
        </Button>
      </div>

      <CallToActions {...callToActions} />

      <Ad {...ad} dataAttributePrefix="user-unauthenticated" />
    </Popover>
  );
};
